/**
 * Pure helpers for resolving which tenant (organization) an impersonated user
 * may reference, and for building the tenant-selector options during an
 * impersonation session.
 *
 * These are intentionally free of store/React dependencies so the behavior can
 * be unit-tested directly (see tenantAccess.test.ts) rather than by standing up
 * a full AuthStore.
 *
 * Context (AUT-10307): while impersonating, every request's `Acx-OrganizationId`
 * header must follow the tenant the impersonated user selected — but only a
 * tenant they are actually authorized for. Anything else (e.g. a stale admin
 * selection persisted in localStorage) must fall back to the impersonated
 * user's home org so the org-access middleware can't lock the session out.
 */

/** Shape returned by /api/TenantAuthorizations/GetTenantAuthorizationsForUser. */
export interface TenantAuthorization {
    id: string;
    orgId: string;
    orgName: string;
    userId: string;
    isActive: boolean;
}

/** Minimal option shape consumed by the tenant selector (id + display name). */
export interface AccessibleTenantOption {
    id: string;
    name: string;
    isActive: boolean;
}

/**
 * The set of organization ids the (impersonated) user may reference: their home
 * org plus every tenant authorization. The home org is always included because
 * access to it does not require a TenantAuthorization row — this mirrors the
 * backend rule in IUserHelper.UserCanAccessOrganization.
 *
 * Note: when tenant authorizations have not loaded yet (undefined), the allowed
 * set is *only* the home org — so the header safely stays on the home tenant
 * during the bootstrap window.
 */
export function buildAllowedOrgIdSet(
    effectiveOrgId: string | undefined,
    tenantAuthorizations: TenantAuthorization[] | undefined,
): Set<string> {
    const allowed = new Set<string>();
    if (effectiveOrgId) {
        allowed.add(effectiveOrgId);
    }
    for (const auth of tenantAuthorizations ?? []) {
        if (auth?.orgId) {
            allowed.add(auth.orgId);
        }
    }
    return allowed;
}

/**
 * Resolve the organization id to send in the request header.
 *
 * While impersonating, only a tenant in the server-computed boundary (the
 * intersection of the acting and impersonated users' access) is allowed through;
 * anything else — including the window before the boundary has loaded — falls back
 * to the impersonated user's home org. The backend validates every request against
 * the acting user regardless, so this is availability handling, not the access
 * control itself. When not impersonating, the requested org is returned unchanged.
 * (AUT-10623)
 */
export function resolveRequestOrganizationId(args: {
    isImpersonating: boolean;
    requestedOrgId: string | undefined;
    effectiveOrgId: string;
    allowedOrgIds: Set<string> | undefined;
}): string {
    const requested = args.requestedOrgId ?? args.effectiveOrgId;

    if (!args.isImpersonating) {
        return requested;
    }

    return args.allowedOrgIds?.has(requested)
        ? requested
        : args.effectiveOrgId;
}

/**
 * Resolve which org id the impersonated session should start on, given a
 * persisted/selected org id. An authorized selection is retained; an
 * unauthorized (or absent) one resets to the home org.
 *
 * `allowedOrgIds === undefined` means "no restriction" (not impersonating): the
 * persisted selection is honored as-is.
 */
export function resolveInitialSelectedOrgId(args: {
    persistedOrgId: string | undefined;
    homeOrgId: string;
    allowedOrgIds: Set<string> | undefined;
}): string {
    if (!args.persistedOrgId) {
        return args.homeOrgId;
    }
    if (!args.allowedOrgIds) {
        return args.persistedOrgId;
    }
    return args.allowedOrgIds.has(args.persistedOrgId)
        ? args.persistedOrgId
        : args.homeOrgId;
}

/**
 * Build the tenant-selector options for an impersonated session from the
 * impersonated user's own authorizations plus their home org.
 *
 * The loaded organization list (orgStore.organizations) is NOT used as the
 * source of membership — it is presentation data that the repro showed can be
 * both over-broad (lists unauthorized tenants) and missing legitimately
 * authorized tenants. It is used only to enrich display name / active state
 * when an entry is present.
 */
export function buildAccessibleTenantOptions(args: {
    effectiveOrgId: string | undefined;
    effectiveOrgName?: string;
    allowedOrgIds: Set<string> | undefined;
    tenantAuthorizations: TenantAuthorization[] | undefined;
    loadedOrganizations:
        | { id: string; name: string; isActive?: boolean }[]
        | undefined;
}): AccessibleTenantOption[] {
    const loadedById = new Map(
        (args.loadedOrganizations ?? []).map((o) => [o.id, o]),
    );
    const options: AccessibleTenantOption[] = [];
    const seen = new Set<string>();

    const pushOption = (
        id: string,
        fallbackName: string,
        fallbackActive: boolean,
    ) => {
        if (!id || seen.has(id)) {
            return;
        }
        // Constrain to the server boundary once it has loaded; the home org is
        // always offered (it is always part of the boundary). (AUT-10623)
        if (
            args.allowedOrgIds &&
            id !== args.effectiveOrgId &&
            !args.allowedOrgIds.has(id)
        ) {
            return;
        }
        seen.add(id);
        const enrich = loadedById.get(id);
        options.push({
            id,
            name: enrich?.name ?? fallbackName,
            isActive: enrich?.isActive ?? fallbackActive,
        });
    };

    if (args.effectiveOrgId) {
        pushOption(args.effectiveOrgId, args.effectiveOrgName ?? "", true);
    }
    for (const auth of args.tenantAuthorizations ?? []) {
        if (auth?.orgId) {
            pushOption(auth.orgId, auth.orgName ?? "", auth.isActive ?? true);
        }
    }

    return options;
}
